/** Per-tenant branding: DB-driven via public API, with static fallbacks for legacy hosts. */

export type TenantThemeColors = {
  primary: string;
  secondary: string;
  light: string;
  dark: string;
  contrastText: string;
};

export type TenantBrandingProfile = {
  tenantId: string;
  storageKey: string;
  companyName: string;
  companyTagline: string;
  logo: string;
  logoMark: string;
  logoOnDark: string;
  themeColors: TenantThemeColors;
  isDefaultTenant?: boolean;
};

export type PublicTenantBrandingDTO = {
  tenant_id: string | null;
  enterprise_id: string | null;
  company_name: string;
  company_tagline: string;
  theme_colors: TenantThemeColors;
  logo: string;
  logo_mark: string;
  logo_on_dark: string;
  is_default_tenant: boolean;
};

const PROVYSN_THEME: TenantThemeColors = {
  primary: '#0078d4',
  secondary: '#106ebe',
  light: '#c7e0f4',
  dark: '#004578',
  contrastText: '#FFFFFF'
};

const MERCURY_THEME: TenantThemeColors = {
  primary: '#3D6B7A',
  secondary: '#5E8C9B',
  light: '#C8DDE6',
  dark: '#1F3A47',
  contrastText: '#FFFFFF'
};

const STATIC_PROFILES: Record<string, TenantBrandingProfile> = {
  provysn: {
    tenantId: 'provysn',
    storageKey: 'provysn-branding',
    companyName: 'PROVYSN.ai',
    companyTagline: 'Identity & Access Management Platform',
    logo: '/favicon-512.png',
    logoMark: '/favicon-512.png',
    logoOnDark: '/favicon-512.png',
    themeColors: PROVYSN_THEME,
    isDefaultTenant: true
  },
  mercury: {
    tenantId: 'mercury',
    storageKey: 'mercury-branding',
    companyName: 'Mercury',
    companyTagline: 'Identity & Access Management Platform',
    logo: '/favicon-512.png',
    logoMark: '/favicon-512.png',
    logoOnDark: '/favicon-512.png',
    themeColors: MERCURY_THEME,
    isDefaultTenant: false
  },
  venus: {
    tenantId: 'venus',
    storageKey: 'venus-branding',
    companyName: 'Venus',
    companyTagline: 'Identity & Access Management Platform',
    logo: '/favicon-512.png',
    logoMark: '/favicon-512.png',
    logoOnDark: '/favicon-512.png',
    themeColors: {
      primary: '#C9A227',
      secondary: '#D4AF37',
      light: '#F5E6B8',
      dark: '#6B4E0F',
      contrastText: '#FFFFFF'
    },
    isDefaultTenant: false
  },
  mars: {
    tenantId: 'mars',
    storageKey: 'mars-branding',
    companyName: 'Mars',
    companyTagline: 'Identity & Access Management Platform',
    logo: '/favicon-512.png',
    logoMark: '/favicon-512.png',
    logoOnDark: '/favicon-512.png',
    themeColors: {
      primary: '#B8402E',
      secondary: '#C8553D',
      light: '#F0D4CF',
      dark: '#5C1F14',
      contrastText: '#FFFFFF'
    },
    isDefaultTenant: false
  },
  jupiter: {
    tenantId: 'jupiter',
    storageKey: 'jupiter-branding',
    companyName: 'Jupiter',
    companyTagline: 'Identity & Access Management Platform',
    logo: '/favicon-512.png',
    logoMark: '/favicon-512.png',
    logoOnDark: '/favicon-512.png',
    themeColors: {
      primary: '#C8762A',
      secondary: '#D9924A',
      light: '#F5E4D0',
      dark: '#5C3A14',
      contrastText: '#FFFFFF'
    },
    isDefaultTenant: false
  },
  default: {
    tenantId: 'default',
    storageKey: 'turboiam-branding',
    companyName: 'TurboIAM',
    companyTagline: 'Identity & Access Management Platform',
    logo: '/favicon-512.png',
    logoMark: '/favicon-512.png',
    logoOnDark: '/favicon-512.png',
    themeColors: PROVYSN_THEME,
    isDefaultTenant: true
  }
};

function brandingCacheKey(): string {
  const host = typeof window !== 'undefined' ? window.location.hostname : 'default';
  return `turboiam-public-branding:${host}`;
}

let cachedProfile: TenantBrandingProfile | null = null;

export function detectTenantIdFromHost(): string {
  const host = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const envTenant = (process.env.REACT_APP_TENANT_ID || '').trim().toLowerCase();
  if (envTenant) return envTenant;
  if (host.includes('mercury')) return 'mercury';
  if (host.includes('venus')) return 'venus';
  if (host.includes('mars')) return 'mars';
  if (host.includes('jupiter')) return 'jupiter';
  if (host === 'demo.provysn.ai' || host.endsWith('.demo.provysn.ai')) return 'provysn';
  if (host.includes('turboiam-provysn')) return 'provysn';
  if (host.includes('provysn') && !host.includes('mercury')) return 'provysn';
  return 'default';
}

/** True on mercury.provysn.ai and other Mercury-hosted frontends. */
export function isMercuryTenant(): boolean {
  return detectTenantIdFromHost() === 'mercury';
}

function profileFromPublicDto(dto: PublicTenantBrandingDTO): TenantBrandingProfile {
  const tenantId = dto.tenant_id || detectTenantIdFromHost();
  return {
    tenantId,
    storageKey: `${tenantId}-branding`,
    companyName: dto.company_name,
    companyTagline: dto.company_tagline,
    logo: dto.logo,
    logoMark: dto.logo_mark,
    logoOnDark: dto.logo_on_dark,
    themeColors: dto.theme_colors,
    isDefaultTenant: dto.is_default_tenant
  };
}

function readCachedPublicBranding(): TenantBrandingProfile | null {
  if (cachedProfile) return cachedProfile;
  try {
    const key = brandingCacheKey();
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    if (raw.length > 100_000) {
      sessionStorage.removeItem(key);
      return null;
    }
    const dto = JSON.parse(raw) as PublicTenantBrandingDTO;
    cachedProfile = profileFromPublicDto(dto);
    return cachedProfile;
  } catch {
    return null;
  }
}

export function cachePublicBranding(dto: PublicTenantBrandingDTO): TenantBrandingProfile {
  const profile = profileFromPublicDto(dto);
  cachedProfile = profile;
  try {
    sessionStorage.setItem(brandingCacheKey(), JSON.stringify(dto));
  } catch {
    /* ignore */
  }
  return profile;
}

/** Load branding from API (call once on app boot / login). */
export async function fetchAndCacheTenantBranding(): Promise<TenantBrandingProfile> {
  try {
    const { fetchAndCacheTenantConfig, configToBrandingProfile } = await import('./tenantConfig');
    const cfg = await fetchAndCacheTenantConfig();
    const profile = configToBrandingProfile(cfg);
    cachedProfile = profile;
    const legacyDto: PublicTenantBrandingDTO = {
      tenant_id: cfg.tenantId,
      enterprise_id: null,
      company_name: cfg.tenantName,
      company_tagline: cfg.welcomeText,
      theme_colors: profile.themeColors,
      logo: cfg.logoUrl,
      logo_mark: cfg.logoMarkUrl,
      logo_on_dark: cfg.logoOnDarkUrl,
      is_default_tenant: cfg.isDefaultTenant
    };
    try {
      const { sanitizeConfigForCache } = await import('./tenantConfig');
      const slim = sanitizeConfigForCache({
        tenantId: cfg.tenantId,
        tenantName: cfg.tenantName,
        logoUrl: cfg.logoUrl,
        logoMarkUrl: cfg.logoMarkUrl,
        logoOnDarkUrl: cfg.logoOnDarkUrl,
        primaryColor: cfg.primaryColor,
        secondaryColor: cfg.secondaryColor,
        welcomeText: cfg.welcomeText,
        loginMethods: cfg.loginMethods,
        ssoProvider: cfg.ssoProvider,
        ssoLoginUrl: cfg.ssoLoginUrl,
        ssoDisabledHint: cfg.ssoDisabledHint,
        supportEmail: cfg.supportEmail,
        isDefaultTenant: cfg.isDefaultTenant
      });
      const cacheDto: PublicTenantBrandingDTO = {
        ...legacyDto,
        logo: slim.logoUrl,
        logo_mark: slim.logoMarkUrl,
        logo_on_dark: slim.logoOnDarkUrl
      };
      sessionStorage.setItem(brandingCacheKey(), JSON.stringify(cacheDto));
    } catch {
      /* ignore */
    }
    return profile;
  } catch {
    const hostId = detectTenantIdFromHost();
    return STATIC_PROFILES[hostId] || STATIC_PROFILES.default;
  }
}

export function getActiveTenantProfile(): TenantBrandingProfile {
  const cached = readCachedPublicBranding();
  if (cached) return cached;
  const hostId = detectTenantIdFromHost();
  return STATIC_PROFILES[hostId] || STATIC_PROFILES.provysn;
}

/** @deprecated use getActiveTenantProfile().tenantId */
export function detectTenantId(): string {
  return getActiveTenantProfile().tenantId;
}

export const TENANT_PROFILES = STATIC_PROFILES;
